import { ApolloError } from '@apollo/client';
import React from 'react';

import {
  SafeQueryPlan,
  containsConnectorFetch,
} from 'src/app/graph/explorerPage/helpers/queryPlanHelpers';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { ConnectorsQueryPlanAlert } from 'src/components/connectorsQueryPlanAlert/ConnectorsQueryPlanAlert';
import { ReadonlyEditor } from 'src/components/editor/ReadonlyEditor';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  Mermaid,
  useMermaidRender,
} from 'src/components/queryPlan/mermaid/Mermaid';
import { SubgraphAndMetadata } from 'src/lib/graphql/subgraphMetadataHelpers';
import { DocsRouter } from 'src/lib/routers';

type QueryPlanProps = {
  mermaidView: boolean;
  graphId: string;
  graphVariant?: string;
  error?: ApolloError;
  loading: boolean;
  mermaidPlanNodeString: string;
  planText?: string;
  queryPlan?: SafeQueryPlan;
  subgraphMap?: Record<string, SubgraphAndMetadata>;
};

export const QueryPlanWindow = ({
  mermaidView,
  graphId,
  graphVariant,
  error,
  loading,
  mermaidPlanNodeString,
  planText,
  queryPlan,
  subgraphMap,
}: QueryPlanProps) => {
  const hasConnector =
    queryPlan &&
    subgraphMap &&
    containsConnectorFetch(queryPlan.node, subgraphMap);

  const mermaidSVGText = useMermaidRender(mermaidPlanNodeString);

  return (
    <>
      {error?.networkError ? (
        <ErrorMessage data-testid="network-error-message" />
      ) : error ? (
        <ErrorMessage>
          <p data-testid="generic-error-message">
            A query plan for this operation could not be generated.
          </p>
          <p className="mt-2">
            This is due to an incompatibility between the operation and your{' '}
            current schema for {graphId}@{graphVariant} in the Apollo Registry,
            for example:{' '}
          </p>
          <ul className="ml-4 mt-2 list-disc">
            <li>
              a recent change to your schema that makes this operation invalid.
            </li>
            <li>
              use of GraphQL language features that are not compatible with{' '}
              Apollo Federation.
            </li>
            <li>
              loss of input type information due to{' '}
              <ClickableText
                as="a"
                className="mt-6 font-semibold underline"
                href={DocsRouter.path('OperationSignatures')}
                rel="noopener noreferrer"
                target="_blank"
              >
                the way that operations are normalized
              </ClickableText>{' '}
              when sent to Studio.
            </li>
          </ul>
        </ErrorMessage>
      ) : loading ? (
        <Loading />
      ) : mermaidView && mermaidPlanNodeString ? (
        <>
          {hasConnector ? <ConnectorsQueryPlanAlert /> : null}
          <Mermaid mermaidSVGText={mermaidSVGText} />
        </>
      ) : !mermaidView && planText !== undefined ? (
        <ReadonlyEditor language="text" src={planText} />
      ) : (
        <NoData
          message="No Query Plan"
          secondary="The query plan could not be evaluated."
        />
      )}
    </>
  );
};
